import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import introJs from 'intro.js';

export type RiesgoSeleccion = {
    nivel: number; // fila elegida (1..4)
    prob: 1 | 2 | 3 | 4; // columna elegida (1..4)
    valor: 1 | 2 | 3 | 4; // 1=BAJO, 2=MEDIO, 3=ALTO, 4=EXTREMO
    titulo: 'BAJO' | 'MEDIO' | 'ALTO' | 'EXTREMO';
};

@Component({
    selector: 'app-riesgo-matriz',
    templateUrl: './riesgo-matriz.component.html',
    styleUrls: ['./riesgo-matriz.component.scss'],
    standalone: false
})
export class RiesgoMatrizComponent implements OnChanges {
    @Input() matrizDatos: any[] = [
        {
            nivel: 4,
            nivel_text: 'Catastrófico',
            lesion: 'Fatalidad o Incapacidad permanente',
            vehicular: 'Incidente vehicular con daños totales > U$S 100K, Fatalidad de CWS o de terceros',
            medioAmbiente: 'Reportable externamente, Costo remediación o multas > U$S 50K, Ingreso de quimicos peligrossos en vias acuíferas',
            propiedadProcesos: 'Costo total de reparación o reemplazo > U$S 250K, Daños a la propiedad de terceros (no vehiculos) causada por CWS > U$S 100K',
            reputacion: 'Cobertura de medios de prensa nacionales. Multas o violaciones > U$S 100K',
            seguridadFisica: 'Agresión física que resulte en una fatalidad o incapacidad permanante, Secuestro/ extorsión, Destrucción/ robo/ fraude de activos de CWS> U$S 100K',
            valorRiesgo: { 1: 3, 2: 4, 3: 4, 4: 4 }
        },
        {
            nivel: 3,
            nivel_text: 'Grave',
            lesion: 'Herida con días caídos',
            vehicular: 'Incidente vehicular con daños totales > U$S 15K pero < U$S 100K, Vuelco o MVI con heridas > que primeros auxilios de personal CWS o de terceros',
            medioAmbiente: 'Reportable externamente, Costo remediación o multas > U$S 10K pero < U$S 50K, Derrame > 8.000 lts (excepto agua limpia)',
            propiedadProcesos: 'Costo total de reparación o reemplazo > U$S 50K pero < U$S 250K, Daños a la propiedad de terceros (no vehiculos) causada por CWS > U$S 25K pero < U$S 100K',
            reputacion: 'Cobertura de medios de prensa regionales. Multas o violaciones > U$S 10K pero < U$S 100K',
            seguridadFisica: 'Asalto que resulta en LTI, Amenaza confirmada con arma o de daño corporal, Comportamiento amenazante (coerción, acecho)/ infracción criminal, Destrucción/ robo/ fraude de activos de CWS > U$S 25K pero < U$S 100K',
            valorRiesgo: { 1: 2, 2: 3, 3: 4, 4: 4 }
        },
        {
            nivel: 2,
            nivel_text: 'Serio',
            lesion: 'Tratamiento médico, Tareas restringidas, Transferencia de puesto',
            vehicular: 'MVI con daños totales > U$S 5K pero < U$S 15K, Heridas con primeros auxilios, Vehículo o conductor de CWS demorado por autoridades',
            medioAmbiente: 'Reportable externamente, Costo remediación o multas > U$S 2.5K pero < U$S 10K, Derrame > 1.600 lts pero < 8.000 lts (excepto agua limpia)',
            propiedadProcesos: 'Costo total de reparación o reemplazo > U$S 10K pero < U$S 50K, Daños a la propiedad de terceros (no vehiculos) causada por CWS < U$S 100K',
            reputacion: 'Cobertura medios de prensa locales. Quejas por escrito del cliente, Multas o violaciones < U$S 10K',
            seguridadFisica: 'Asalto que resulta en tratamiento médico, Amenaza implícita con arma o daño corporal, Destrucción/ robo/ fraude de los activos de CWS > U$S 5K pero < U$S 25K, Otros delitos menores',
            valorRiesgo: { 1: 1, 2: 2, 3: 3, 4: 4 }
        },
        {
            nivel: 1,
            nivel_text: 'Menor',
            lesion: 'Primeros Auxilios',
            vehicular: 'MVI con daños totales < U$S 5K, Multa a vehículo de CWS',
            medioAmbiente: 'No reportable, Costo remediación < U$S 2.5K, Derrame < 1.600 lts',
            propiedadProcesos: 'Costo total de reparación, reemplazo < U$S 10K',
            reputacion: 'Nota: Las multas o violaciones establecidas más arriba excluyen las multas ambientales',
            seguridadFisica: 'Asalto que resulta en primeros auxilios, comportamiento molesto, Destruccion/ robo/ fraude de bienes de CWS < U$S 5K',
            valorRiesgo: { 1: 1, 2: 1, 3: 2, 4: 3 }
        }
    ];

    @Input() descripcionProbabilidades = [
        { valor: 4, etiqueta: 'Frecuente', descripcion: 'Ocurre la mayoría de las veces. (Diario o varias veces en la semana)' },
        { valor: 3, etiqueta: 'Probable', descripcion: 'Ocurre cada cierto tiempo. (Semanal o un par de veces al mes)' },
        { valor: 2, etiqueta: 'Improbable', descripcion: 'Ocurre rara vez. (Una vez al año)' },
        { valor: 1, etiqueta: 'Remoto', descripcion: 'Puede ocurrir alguna vez. (Una vez cada 5 años)' }
    ];

    @Input() descripcionRiesgo = [
        { valor: 4, titulo: 'EXTREMO', descripcion: 'Detener actividad hasta implementar controles de riesgo', color: 'bg-red-600' },
        { valor: 3, titulo: 'ALTO', descripcion: 'Se deben implementar controles extensivos', color: 'bg-orange-400' },
        { valor: 2, titulo: 'MEDIO', descripcion: 'Riesgo razonable, requiere mitigación', color: 'bg-yellow-400' },
        { valor: 1, titulo: 'BAJO', descripcion: 'Riesgo aceptable, no requiere controles adicionales', color: 'bg-green-500' }
    ];

    /** ✅ para repintar si ya había una selección previa */
    @Input() seleccionInicial: { nivel: number; prob: 1 | 2 | 3 | 4 } | null = null;

    /** ✅ emite al padre cuando se confirma */
    @Output() confirmar = new EventEmitter<RiesgoSeleccion>();

    /** selección actual (UI) */
    seleccion: { nivel: number; prob: 1 | 2 | 3 | 4 } | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['seleccionInicial']) {
            this.seleccion = this.seleccionInicial ? { ...this.seleccionInicial } : null;
        }
    }

    estaSeleccionado(nivel: number, prob: number): boolean {
        return this.seleccion?.nivel === nivel && this.seleccion?.prob === (prob as 1 | 2 | 3 | 4);
    }

    seleccionar(nivel: number, prob: number): void {
        this.seleccion = { nivel, prob: prob as 1 | 2 | 3 | 4 };
    }

    private getValorRiesgoSeleccionado(): 1 | 2 | 3 | 4 | null {
        if (!this.seleccion) return null;
        const fila = this.matrizDatos.find((f) => f.nivel === this.seleccion!.nivel);
        if (!fila) return null;
        const v = fila.valorRiesgo?.[this.seleccion!.prob];
        return (v ?? null) as 1 | 2 | 3 | 4 | null;
    }

    private getTituloRiesgo(valor: 1 | 2 | 3 | 4 | null): 'BAJO' | 'MEDIO' | 'ALTO' | 'EXTREMO' | null {
        if (valor == null) return null;
        const t = this.descripcionRiesgo.find((r) => r.valor === valor)?.titulo;
        return (t ?? null) as any;
    }

    guardar(): void {
        if (!this.esValido() || !this.seleccion) return;
        const valor = this.getValorRiesgoSeleccionado(); // 1..4
        const titulo = this.getTituloRiesgo(valor); // BAJO..EXTREMO
        if (valor && titulo) {
            this.confirmar.emit({
                nivel: this.seleccion.nivel,
                prob: this.seleccion.prob,
                valor,
                titulo
            });
        }
        // El cierre del p-dialog lo hace el padre (Step2)
    }

    esValido(): boolean {
        return !!this.seleccion; // obliga a elegir una probabilidad
    }

    iniciarGuia(): void {
        introJs()
            .setOptions({
                tooltipClass: 'custom-tooltip',
                buttonClass: 'p-button p-component',
                steps: [
                    { element: '#step1', title: 'Matríz de Riesgo', intro: 'Esta es la matriz de evaluación de riesgo. Aquí podés seleccionar el riesgo potencial combinando las consecuencias y la probabilidad.' },
                    { element: '#step2', title: 'Causas Potenciales', intro: 'Las columnas muestran las categorías de causas potenciales.' },
                    { element: '#step3', title: 'Ver Contenido Completo', intro: 'Hacé clic en los textos para ver el contenido completo de las causas potenciales.' },
                    { element: '#step4', title: 'Probabilidad de Ocurrencia', intro: 'Debe marcar la opción que más se ajuste a su caso.' }
                ],
                showBullets: true,
                nextLabel: 'Siguiente',
                prevLabel: 'Anterior',
                doneLabel: 'Listo'
            })
            .start();
    }
}
